import { Fragment, useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLazyQuery, useQuery } from '@apollo/client'
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditIcon from '@mui/icons-material/Edit'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Button } from 'ui/baseComponents'
import Loading from 'ui/components/Loading'
import AdminPageHeader from 'ui/screens/Admin/AdminPageHeader'
import { formatDateString } from 'utilities/time'
import {
  GET_CAMPAIGN,
  GET_OUTREACH_CAMPAIGN_STATS,
  GET_OUTREACH_HISTORY_LIST,
} from '../graphql/campaignQueries'
import { useTagList } from '../graphql/tagQueries'
import { ROUTE_METADATA } from 'app/AppRoutes/RouteMetadata'
import { TEMPLATE_CHANNELS } from '../consts'
import TemplatePreview from '../tabs/TemplatesTab/TemplatePreview'
import { InfoTooltip } from '../components/OutreachStatsCard'
import DeliveryForecastHeatmap from '../components/DeliveryForecastHeatmap'
import { getHistoryStepLabel, getRemovedStepLabel } from '../stepLabels'

const channelLabel = (channel) =>
  TEMPLATE_CHANNELS.find((c) => c.value === channel)?.label ?? channel

const PAGE_SIZE = 50

const STATUS_COLORS = {
  sent: 'success',
  failed: 'error',
}

const CampaignDashboard = () => {
  const { campaignId } = useParams()
  const navigate = useNavigate()

  const [campaign, setCampaign] = useState(null)
  const [history, setHistory] = useState([])
  const [endCursor, setEndCursor] = useState(null)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)
  const [previewTemplate, setPreviewTemplate] = useState(null)
  const [heatmapFilterDate, setHeatmapFilterDate] = useState(null)
  const [heatmapFilterContentId, setHeatmapFilterContentId] = useState(null)
  const [filterStep, setFilterStep] = useState('')
  const [filterChannel, setFilterChannel] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterMessageId, setFilterMessageId] = useState('')

  const { data: tagsData } = useTagList()
  const tags = tagsData?.tags || []
  const tagMap = Object.fromEntries(tags.map((t) => [t.id, t.tag]))

  const onCampaignCompleted = useCallback((data) => {
    if (data?.campaign) setCampaign(data.campaign)
  }, [])

  const onCampaignError = useCallback(
    () => setError('Failed to load campaign'),
    [],
  )

  const [fetchCampaign, { loading: loadingCampaign }] = useLazyQuery(
    GET_CAMPAIGN,
    {
      fetchPolicy: 'network-only',
      onCompleted: onCampaignCompleted,
      onError: onCampaignError,
    },
  )

  const { data: statsData } = useQuery(GET_OUTREACH_CAMPAIGN_STATS, {
    variables: { campaignId },
    fetchPolicy: 'cache-and-network',
    skip: !campaignId,
  })

  const onHistoryCompleted = useCallback((data) => {
    const items = data?.outreachHistoryList?.history || []
    const cursor = data?.outreachHistoryList?.pageInfo?.endCursor
    setHistory((prev) => [...prev, ...items])
    setEndCursor(cursor || null)
  }, [])

  const onHistoryError = useCallback(
    () => setError('Failed to load outreach history'),
    [],
  )

  const [fetchHistory, { loading: loadingHistory }] = useLazyQuery(
    GET_OUTREACH_HISTORY_LIST,
    {
      fetchPolicy: 'network-only',
      onCompleted: onHistoryCompleted,
      onError: onHistoryError,
    },
  )

  useEffect(() => {
    if (campaignId) {
      setHistory([])
      setEndCursor(null)
      fetchCampaign({ variables: { id: campaignId } })
      fetchHistory({ variables: { filters: { campaignId }, limit: PAGE_SIZE } })
    }
    // fetchCampaign and fetchHistory are stable refs from useLazyQuery
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId])

  const handleLoadMore = useCallback(() => {
    if (endCursor) {
      fetchHistory({
        variables: {
          filters: { campaignId },
          cursor: endCursor,
          limit: PAGE_SIZE,
        },
      })
    }
  }, [campaignId, endCursor, fetchHistory])

  const handleCopyId = useCallback(() => {
    navigator.clipboard
      .writeText(campaignId)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      })
      .catch((err) => console.error('Failed to copy:', err))
  }, [campaignId])

  if (loadingCampaign && !campaign) return <Loading />

  if (!campaign && !loadingCampaign) {
    return <Alert severity="error">Campaign not found</Alert>
  }

  if (!campaign) return null

  const sortedContent = [...(campaign.campaignContent || [])].sort(
    (a, b) => a.sequence - b.sequence,
  )

  const stats = statsData?.outreachCampaignStats ?? null

  const stepLabelByContentId = Object.fromEntries(
    sortedContent.map((c) => [c.id, c.template?.name ?? c.templateId]),
  )

  const templateByContentId = Object.fromEntries(
    sortedContent.map((c) => [c.id, c.template ?? null]),
  )

  const contentStepRows = (() => {
    if (!stats?.byContent?.length) return []
    const rows = sortedContent
      .map((content) => ({
        key: content.id,
        label: content.template?.name ?? content.templateId,
        template: content.template ?? null,
        row: stats.byContent.find((b) => b.campaignContentId === content.id),
      }))
      .filter((r) => r.row)
    // Retired steps keep their id and history; list them after the active steps.
    stats.byContent
      .filter((b) => b.isRemoved)
      .forEach((b) => {
        rows.push({
          key: b.campaignContentId,
          label: getRemovedStepLabel(b.templateName, b.campaignContentId),
          template: null,
          row: b,
        })
      })
    // Legacy history written before steps were retired instead of deleted.
    const orphan = stats.byContent.find((b) => b.campaignContentId == null)
    if (orphan) {
      rows.push({
        key: 'orphan',
        label: 'Deleted step',
        template: null,
        row: orphan,
      })
    }
    return rows
  })()

  return (
    <>
      <AdminPageHeader>
        <Typography variant="h5" component="h1">
          Campaign Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            type="secondary"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(ROUTE_METADATA.adminOutreachCampaigns.path)}
          >
            Back to Campaigns
          </Button>
          <Button
            type="primary"
            size="small"
            startIcon={<EditIcon />}
            onClick={() =>
              navigate(
                `${ROUTE_METADATA.adminOutreachCampaigns.path}/${campaignId}/edit`,
              )
            }
          >
            Edit Campaign
          </Button>
        </Box>
      </AdminPageHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Campaign summary */}
      <Card elevation={0} sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 4,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" component="h2" sx={{ mb: 0.5 }}>
                {campaign.name}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 1,
                }}
              >
                <Chip
                  label={campaign.active ? 'Active' : 'Inactive'}
                  size="small"
                  color={campaign.active ? 'success' : 'default'}
                />
                <Typography variant="body2" color="text.secondary">
                  Priority: {campaign.priority}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 4,
                  mb: 1,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    ID
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '& .copy-id-icon': { opacity: 0 },
                      '&:hover .copy-id-icon': { opacity: 1 },
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {campaignId}
                    </Typography>
                    <IconButton
                      className="copy-id-icon"
                      size="small"
                      onClick={handleCopyId}
                      sx={{ p: 0, transition: 'opacity 0.15s' }}
                      title="Copy campaign ID"
                      aria-label="Copy campaign ID"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    {copied && (
                      <Typography
                        variant="body2"
                        color="success.main"
                        sx={{ ml: 0.5 }}
                      >
                        Copied!
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Tag
                  </Typography>
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
                    onClick={() =>
                      navigate(
                        `${ROUTE_METADATA.adminOutreachTags.path}/${campaign.tagId}`,
                      )
                    }
                  >
                    {tagMap[campaign.tagId] || campaign.tagId}
                  </Typography>
                </Box>
              </Box>

              {campaign.description && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body2">
                    {campaign.description}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Right side: Dates and stats */}
            <Box sx={{ display: 'flex', gap: 4, pt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Start Date
                </Typography>
                <Typography variant="body2">
                  {campaign.startDate
                    ? formatDateString({
                        dateString: campaign.startDate,
                        format: 'MMM DD, YYYY',
                      })
                    : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  End Date
                </Typography>
                <Typography variant="body2">
                  {campaign.endDate
                    ? formatDateString({
                        dateString: campaign.endDate,
                        format: 'MMM DD, YYYY',
                      })
                    : '-'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Content sequence summary */}
      <Card elevation={0} sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Content Sequence
          </Typography>
          {sortedContent.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No content steps configured
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {sortedContent.map((item, index) => (
                <Fragment key={item.id}>
                  {index > 0 && item.intervalHours != null && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mx: 0.5 }}
                    >
                      {(() => {
                        const d = Math.floor(item.intervalHours / 24)
                        const h = item.intervalHours % 24
                        if (d > 0 && h > 0) return `+${d}d ${h}h`
                        if (d > 0) return `+${d}d`
                        return `+${h}h`
                      })()}
                    </Typography>
                  )}
                  <Chip
                    label={`${index + 1}. ${
                      item.template?.name || item.templateId
                    } (${
                      item.template?.channel
                        ? channelLabel(item.template.channel)
                        : '?'
                    })`}
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      item.template
                        ? setPreviewTemplate(item.template)
                        : navigate(
                            `${ROUTE_METADATA.adminOutreachTemplates.path}/${item.templateId}`,
                          )
                    }
                    sx={{ cursor: 'pointer' }}
                  />
                </Fragment>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Stats cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'stretch' }}>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            flex: 2,
            textAlign: 'center',
            position: 'relative',
            '& .info-icon': { opacity: 0 },
            '&:hover .info-icon': { opacity: 1 },
          }}
        >
          <InfoTooltip title="Distinct patients with at least one successfully delivered message (sent status) out of all patients attempted (any status).">
            <InfoOutlinedIcon
              className="info-icon"
              sx={{
                fontSize: '16px !important',
                color: 'text.disabled',
                transition: 'opacity 0.15s',
                position: 'absolute',
                top: 6,
                right: 6,
              }}
            />
          </InfoTooltip>
          <Typography variant="h4">
            {stats
              ? `${stats.uniqueDelivered} / ${stats.uniqueAttempted}`
              : '—'}
          </Typography>
          <Typography
            variant="caption"
            color="success.main"
            sx={{
              display: 'block',
              visibility: stats?.uniqueAttempted > 0 ? 'visible' : 'hidden',
            }}
          >
            {stats?.uniqueAttempted > 0
              ? `${Math.round(
                  (stats.uniqueDelivered / stats.uniqueAttempted) * 100,
                )}%`
              : '0%'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Patients Reached
          </Typography>
        </Paper>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            flex: 1,
            textAlign: 'center',
            position: 'relative',
            '& .info-icon': { opacity: 0 },
            '&:hover .info-icon': { opacity: 1 },
          }}
        >
          <InfoTooltip title="Total messages successfully delivered (sent status). One patient may account for multiple messages across content steps or channels.">
            <InfoOutlinedIcon
              className="info-icon"
              sx={{
                fontSize: '16px !important',
                color: 'text.disabled',
                transition: 'opacity 0.15s',
                position: 'absolute',
                top: 6,
                right: 6,
              }}
            />
          </InfoTooltip>
          <Typography variant="h4" color="success.main">
            {stats?.sent ?? '—'}
          </Typography>
          {stats?.totalMessages > 0 && (
            <Typography variant="caption" color="text.secondary">
              {Math.round((stats.sent / stats.totalMessages) * 100)}%
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Messages Delivered
          </Typography>
        </Paper>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            flex: 1,
            textAlign: 'center',
            position: 'relative',
            '& .info-icon': { opacity: 0 },
            '&:hover .info-icon': { opacity: 1 },
          }}
        >
          <InfoTooltip title="Messages that failed to send due to a transient or unclassified error. May succeed on retry.">
            <InfoOutlinedIcon
              className="info-icon"
              sx={{
                fontSize: '16px !important',
                color: 'text.disabled',
                transition: 'opacity 0.15s',
                position: 'absolute',
                top: 6,
                right: 6,
              }}
            />
          </InfoTooltip>
          <Typography
            variant="h4"
            color={stats?.failed > 0 ? 'error.main' : null}
          >
            {stats?.failed ?? '—'}
          </Typography>
          {stats?.totalMessages > 0 && (
            <Typography variant="caption" color="text.secondary">
              {Math.round((stats.failed / stats.totalMessages) * 100)}%
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Failed
          </Typography>
        </Paper>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            flex: 1,
            textAlign: 'center',
            position: 'relative',
            '& .info-icon': { opacity: 0 },
            '&:hover .info-icon': { opacity: 1 },
          }}
        >
          <InfoTooltip title="Messages where delivery is permanently impossible: the patient has no contact record in Intercom, or the email hard-bounced at the provider.">
            <InfoOutlinedIcon
              className="info-icon"
              sx={{
                fontSize: '16px !important',
                color: 'text.disabled',
                transition: 'opacity 0.15s',
                position: 'absolute',
                top: 6,
                right: 6,
              }}
            />
          </InfoTooltip>
          <Typography variant="h4">{stats?.undeliverable ?? '—'}</Typography>
          {stats?.totalMessages > 0 && (
            <Typography variant="caption" color="text.secondary">
              {Math.round((stats.undeliverable / stats.totalMessages) * 100)}%
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Undeliverable
          </Typography>
        </Paper>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            flex: 1,
            textAlign: 'center',
            position: 'relative',
            '& .info-icon': { opacity: 0 },
            '&:hover .info-icon': { opacity: 1 },
          }}
        >
          <InfoTooltip title="Messages that failed to send because the patient was already unsubscribed at the time of sending. This is not the count of patients who unsubscribed as a result of receiving a message — Intercom tracks that separately.">
            <InfoOutlinedIcon
              className="info-icon"
              sx={{
                fontSize: '16px !important',
                color: 'text.disabled',
                transition: 'opacity 0.15s',
                position: 'absolute',
                top: 6,
                right: 6,
              }}
            />
          </InfoTooltip>
          <Typography variant="h4">{stats?.unsubscribed ?? '—'}</Typography>
          {stats?.totalMessages > 0 && (
            <Typography variant="caption" color="text.secondary">
              {Math.round((stats.unsubscribed / stats.totalMessages) * 100)}%
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Unsubscribed
          </Typography>
        </Paper>
      </Box>

      {/* Stats by content step */}
      {contentStepRows.length > 0 && (
        <Card elevation={0} sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Stats by Content Step
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Step</TableCell>
                    <TableCell align="center">Total</TableCell>
                    <TableCell align="center">Patients Reached</TableCell>
                    <TableCell align="center">Sent</TableCell>
                    <TableCell align="center">Failed</TableCell>
                    <TableCell align="center">Undeliverable</TableCell>
                    <TableCell align="center">Unsubscribed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contentStepRows.map(({ key, label, template, row }) => {
                    const pct = (n) =>
                      row.total > 0
                        ? ` (${Math.round((n / row.total) * 100)}%)`
                        : ''
                    return (
                      <TableRow key={key}>
                        <TableCell>
                          {template ? (
                            <Typography
                              variant="body2"
                              color="primary"
                              sx={{
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.7 },
                              }}
                              onClick={() => setPreviewTemplate(template)}
                            >
                              {label}
                            </Typography>
                          ) : (
                            label
                          )}
                        </TableCell>
                        <TableCell align="center">{row.total}</TableCell>
                        <TableCell align="center">
                          {row.uniqueDelivered} / {row.uniqueAttempted}
                        </TableCell>
                        <TableCell align="center">
                          {row.sent}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                          >
                            {pct(row.sent)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {row.failed}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                          >
                            {pct(row.failed)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {row.undeliverable}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                          >
                            {pct(row.undeliverable)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {row.unsubscribed}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                          >
                            {pct(row.unsubscribed)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* History table */}
      <Card elevation={0}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Outreach History
          </Typography>
          <DeliveryForecastHeatmap
            campaignId={campaignId}
            campaignContent={sortedContent}
            selectedDate={heatmapFilterDate}
            selectedContentId={heatmapFilterContentId}
            onSelectDate={(date) => {
              setHeatmapFilterDate(date)
              if (!date) setHeatmapFilterContentId(null)
            }}
            onSelectCell={(contentId) => setHeatmapFilterContentId(contentId)}
          />
          <Box sx={{ mt: 3 }}>
            {/* Filter bar */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Autocomplete
                value={
                  filterStep
                    ? sortedContent.find((c) => c.id === filterStep) ?? null
                    : null
                }
                onChange={(_, v) => setFilterStep(v?.id ?? '')}
                options={sortedContent}
                getOptionLabel={(c) => c.template?.name ?? c.id}
                size="small"
                sx={{ minWidth: 180 }}
                componentsProps={{
                  paper: {
                    elevation: 1,
                    sx: { border: '1px solid', borderColor: 'divider' },
                  },
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="All steps" />
                )}
              />
              <Autocomplete
                value={
                  filterChannel
                    ? TEMPLATE_CHANNELS.find(
                        (c) => c.value === filterChannel,
                      ) ?? null
                    : null
                }
                onChange={(_, v) => setFilterChannel(v?.value ?? '')}
                options={TEMPLATE_CHANNELS}
                getOptionLabel={(c) => c.label}
                size="small"
                sx={{ minWidth: 150 }}
                componentsProps={{
                  paper: {
                    elevation: 1,
                    sx: { border: '1px solid', borderColor: 'divider' },
                  },
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="All channels" />
                )}
              />
              <Autocomplete
                value={filterStatus || null}
                onChange={(_, v) => setFilterStatus(v ?? '')}
                options={[
                  'sent',
                  'failed',
                  'undeliverable',
                  'unsubscribed',
                  'unknown',
                ]}
                size="small"
                sx={{ minWidth: 150 }}
                componentsProps={{
                  paper: {
                    elevation: 1,
                    sx: { border: '1px solid', borderColor: 'divider' },
                  },
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="All statuses" />
                )}
              />
              <TextField
                placeholder="Message ID"
                value={filterMessageId}
                onChange={(e) => setFilterMessageId(e.target.value)}
                size="small"
                sx={{ minWidth: 180, fontSize: 13 }}
              />
              {heatmapFilterDate && (
                <Chip
                  label={
                    heatmapFilterContentId
                      ? `${heatmapFilterDate} · ${
                          stepLabelByContentId[heatmapFilterContentId] ??
                          'Unknown step'
                        }`
                      : heatmapFilterDate
                  }
                  size="small"
                  onDelete={() => {
                    setHeatmapFilterDate(null)
                    setHeatmapFilterContentId(null)
                  }}
                />
              )}
            </Box>
            {(() => {
              const hasFilters =
                heatmapFilterDate ||
                filterStep ||
                filterChannel ||
                filterStatus ||
                filterMessageId
              const filteredHistory = history.filter((item) => {
                if (heatmapFilterDate) {
                  const itemDate = (() => {
                    if (!item.sentAt) return null
                    const d = new Date(item.sentAt * 1000)
                    const y = d.getFullYear()
                    const m = String(d.getMonth() + 1).padStart(2, '0')
                    const day = String(d.getDate()).padStart(2, '0')
                    return `${y}-${m}-${day}`
                  })()
                  if (itemDate !== heatmapFilterDate) return false
                  if (
                    heatmapFilterContentId &&
                    item.campaignContentId !== heatmapFilterContentId
                  )
                    return false
                }
                if (filterStep && item.campaignContentId !== filterStep)
                  return false
                if (filterChannel && item.channel !== filterChannel)
                  return false
                if (filterStatus && item.deliveryStatus !== filterStatus)
                  return false
                if (
                  filterMessageId &&
                  !item.messageId
                    ?.toLowerCase()
                    .includes(filterMessageId.toLowerCase())
                )
                  return false
                return true
              })

              if (history.length === 0 && !loadingHistory) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    No outreach history for this campaign yet
                  </Typography>
                )
              }

              return (
                <TableContainer sx={{ minHeight: 858 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Patient ID</TableCell>
                        <TableCell>Step</TableCell>
                        <TableCell>Channel</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Sent At</TableCell>
                        <TableCell>Message ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredHistory.length === 0 && !loadingHistory ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            align="center"
                            sx={{ py: 4, color: 'text.secondary' }}
                          >
                            {hasFilters
                              ? 'No rows match the current filters. Try removing some filters.'
                              : 'No outreach history for this campaign yet.'}
                          </TableCell>
                        </TableRow>
                      ) : null}
                      {filteredHistory.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {item.patientId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {item.campaignContentId ? (
                              templateByContentId[item.campaignContentId] ? (
                                <Typography
                                  variant="body2"
                                  color="primary"
                                  sx={{
                                    cursor: 'pointer',
                                    '&:hover': { opacity: 0.7 },
                                  }}
                                  onClick={() =>
                                    setPreviewTemplate(
                                      templateByContentId[
                                        item.campaignContentId
                                      ],
                                    )
                                  }
                                >
                                  {stepLabelByContentId[item.campaignContentId]}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {getHistoryStepLabel(item)}
                                </Typography>
                              )
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={channelLabel(item.channel)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.deliveryStatus}
                              size="small"
                              color={
                                STATUS_COLORS[item.deliveryStatus] || 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {item.sentAt
                              ? new Date(item.sentAt * 1000).toLocaleString()
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontFamily: 'monospace', fontSize: 12 }}
                            >
                              {item.messageId || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )
            })()}

            {loadingHistory && <Loading />}

            {endCursor && !loadingHistory && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button type="secondary" size="small" onClick={handleLoadMore}>
                  Load More
                </Button>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      <TemplatePreview
        template={previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        onOpenFull={
          previewTemplate
            ? () => {
                navigate(
                  `${ROUTE_METADATA.adminOutreachTemplates.path}/${previewTemplate.id}`,
                )
                setPreviewTemplate(null)
              }
            : null
        }
      />
    </>
  )
}

export default CampaignDashboard
